import { Injectable } from "@angular/core";
import { ItemOwnerWithRelations } from "src/app/api/models";

@Injectable({
  providedIn: "root",
})
export class OwnerService {
  constructor() {}

  /**
   * Retrieves the year of purchase for the given item owner.
   *
   * This method returns an object containing a label and a tooltip. The label is either the year of purchase text
   * or the year of purchase as a string. The tooltip provides additional context about the date's accuracy.
   *
   * @param itemOwner - The ItemOwnerWithRelations object.
   * @returns An object containing the label and tooltip for the year of purchase.
   */
  getYearOfPurchase(itemOwner: ItemOwnerWithRelations): {
    label: string;
    tooltip: string;
  } {
    let label = "";
    let tooltip = "";
    if (itemOwner.yearPurchaseText) {
      // If yearPurchaseText is available, use it as the label and set the tooltip to "Přesné datum".
      label = itemOwner.yearPurchaseText;
      tooltip = "Přesné datum";
    } else if (itemOwner.yearPurchase) {
      // If yearPurchase is available, use it as the label and set the tooltip to "Přesné datum není známo".
      label = itemOwner.yearPurchase.toString();
      tooltip = "Přesné datum není známo";
    }
    return { label, tooltip };
  }

  /**
   * Parses the title of the owner based on the specified type.
   *
   * This method constructs the title string according to the provided type parameter.
   * It returns either the owner's name (contact title) or the owner's contact type title.
   *
   * @param itemOwner - The ItemOwnerWithRelations object.
   * @param type - The type of title to parse ("name" or "type").
   * @returns The parsed owner title string.
   */
  parseOwnerTitle(
    itemOwner: ItemOwnerWithRelations,
    type: "name" | "type"
  ): string {
    let title = "";

    // Check for special contactId cases.
    // If the contactId is "unknown", return a default title indicating the owner is unknown.
    switch (itemOwner.contactId) {
      case "unknown":
        title = "Nezjištěný majitel";
        break;
      case "anonym":
        // If the contactId is "anonym", return a default title indicating the owner is anonymous.
        title = "Anonymní majitel";
        break;
      default:
        // For any other contactId value, determine the title based on the specified type.
        switch (type) {
          case "name":
            // If type is "name", return the contact's title (i.e. the owner's name).
            title = itemOwner.contact?.title || "";
            break;
          case "type":
            // If type is "type", return the title of the contact type associated with the owner.
            title = itemOwner.contact?.contactType?.title || "";
            break;
          default:
            // No action needed if type doesn't match expected values.
            break;
        }
        break;
    }

    return title;
  }
}
